import { CheckoutWidgetsBuilderState } from "@redotech/redo-conversion-core-common/checkouts/checkout-widgets-builder";
import {
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  SHIPPING_OPTION_IMAGE_WIDTH_FULL,
  ShippingOptionRenderTarget,
} from "@redotech/redo-conversion-core-common/checkouts/widget";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { CheckoutWidgetsBuilderContext } from "../checkout-widgets-builder-context";
import { ExampleShippingOption } from "./render-after-shipping-option-widget-preview";
import * as css from "./render-after-shipping-option-widget-preview.module.css";

type ShippingOptionImageWidgetData = NonNullable<
  CheckoutWidgetsBuilderState["checkoutWidgets"][CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]
>;

const imageJustificationToFlexJustify = {
  [CheckoutImageBlockJustification.LEFT]: "flex-start",
  [CheckoutImageBlockJustification.CENTER]: "center",
  [CheckoutImageBlockJustification.RIGHT]: "flex-end",
} as const;

export const ShippingOptionImageWidgetPreview = memo(
  function ShippingOptionImageWidgetPreview() {
    const { checkoutWidgets } = useContext(CheckoutWidgetsBuilderContext);

    const widget = useMemo(
      () =>
        checkoutWidgets[CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE],
      [checkoutWidgets],
    );

    if (!widget) {
      return null;
    }

    return widget.target === ShippingOptionRenderTarget.DETAILS_RENDER_AFTER ? (
      <DetailsImagePreview widget={widget} />
    ) : (
      <RenderAfterImagePreview widget={widget} />
    );
  },
);

function getImageWidthStyle(imageWidth: string): string {
  return imageWidth === SHIPPING_OPTION_IMAGE_WIDTH_FULL
    ? "100%"
    : `${imageWidth}px`;
}

const ImagePreviewContent = memo(function ImagePreviewContent({
  widget,
}: {
  widget: ShippingOptionImageWidgetData;
}) {
  const widthStyle = getImageWidthStyle(widget.imageWidth);

  return (
    <Flex
      justify={imageJustificationToFlexJustify[widget.imageJustification]}
      p="md"
      w="full"
    >
      {widget.imageUrl ? (
        <img
          alt="Shipping option image preview"
          src={widget.imageUrl}
          style={{
            width: widthStyle,
            borderRadius: "var(--border-radius-sm)",
            maxWidth: "100%",
          }}
        />
      ) : (
        <Flex
          align="center"
          bgColor="tertiary"
          borderRadius="md"
          justify="center"
          px="md"
          py="3xl"
          style={{ width: widthStyle, maxWidth: "100%" }}
        >
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Upload an image to preview
          </Text>
        </Flex>
      )}
    </Flex>
  );
});

const DetailsImagePreview = memo(function DetailsImagePreview({
  widget,
}: {
  widget: ShippingOptionImageWidgetData;
}) {
  return (
    <ExampleShippingOption bottomRadii="lg" topRadii="lg">
      <ImagePreviewContent widget={widget} />
    </ExampleShippingOption>
  );
});

const RenderAfterImagePreview = memo(function RenderAfterImagePreview({
  widget,
}: {
  widget: ShippingOptionImageWidgetData;
}) {
  return (
    <Flex className={css.shippingOptionWidgetPreview} dir="column" gap="none">
      <Flex
        borderTopLeftRadius="lg"
        borderTopRightRadius="lg"
        className={css.renderAfterShippingOptionWidgetPreviewSelected}
        dir="column"
        gap="xl"
        p="xl"
      >
        <RenderAfterExampleShippingOptionContent selected />
      </Flex>
      <Flex
        bgColor="tertiary"
        borderColor="primary"
        borderStyle="solid"
        borderWidth="1px"
        p="xl"
      >
        <ImagePreviewContent widget={widget} />
      </Flex>
      <ExampleShippingOption bottomRadii="none" topRadii="none" />
      <ExampleShippingOption bottomRadii="lg" topRadii="none" />
    </Flex>
  );
});

const RenderAfterExampleShippingOptionContent = memo(
  function RenderAfterExampleShippingOptionContent({
    selected = false,
  }: {
    selected?: boolean;
  }) {
    return (
      <Flex align="center" dir="row" justify="space-between">
        <Flex align="center" dir="row" gap="xl">
          {selected ? (
            <Flex borderRadius="full" className={css.selectedCircle} />
          ) : (
            <Flex
              borderColor="primary"
              borderRadius="full"
              borderStyle="solid"
              borderWidth="1px"
              className={css.grayCircle}
            />
          )}
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              [Shipping name]
            </Text>
            <Text fontSize="xs" textColor="secondary">
              [Shipping description]
            </Text>
          </Flex>
        </Flex>
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          [Price]
        </Text>
      </Flex>
    );
  },
);
